/**
 * Processes the substance upload queue.
 * Handles uploading files to the server and updating queue status.
 */

import type { ApiClient } from '../store/backend';
import { t } from 'src/i18n';
import { getBasePath } from '../store/backend';
import { getValidAccessToken } from '../store/oauth-browser';
import { AUTH_ADMIN_TOKEN_KEY } from '../auth/auth-storage';
import type { UploadedFile } from '../pages/substances-page/uploaded-file';
import type { QueuedUpload } from './substance-upload-types';
import {
  getPendingUploads,
  getFailedUploads,
  updateUploadStatus,
  removeUpload,
  queuedUploadToFile,
  notifyQueueChanged,
  removeExpiredDuplicates,
  removeExpiredFailedUploads,
} from './substance-upload-queue';

let isProcessing = false;
let currentApi: ApiClient | null = null;

/**
 * Get authorization header for uploads.
 * Supports both admin login (Basic auth) and OAuth login (Bearer token).
 * Fix for issue #12431 - admin login was not working for uploads.
 *
 * Note: Admin login is web-only, so we always use localStorage for admin token.
 * OAuth tokens use SecureStorage on native platforms (handled by getValidAccessToken).
 */
async function getAuthorizationHeader(): Promise<string> {
  // Check for admin token first (Basic auth) - admin login is web-only
  const adminToken = localStorage.getItem(AUTH_ADMIN_TOKEN_KEY);
  if (adminToken) {
    return `Basic ${adminToken}`;
  }

  // Fall back to OAuth token (Bearer auth)
  const accessToken = await getValidAccessToken();
  if (!accessToken) {
    throw new Error('No authorization available - please log in again');
  }

  return `Bearer ${accessToken}`;
}

/**
 * Set the API client to use for uploads
 */
export function setApiClient(api: ApiClient): void {
  currentApi = api;
}

/**
 * Process all pending uploads for an organization
 * Returns the results of successfully uploaded files
 *
 * If new files are added while processing, they will be processed after the current batch.
 */
export async function processUploads(
  organizationId: string,
  onProgress?: (completed: number, total: number) => void,
): Promise<UploadedFile[]> {
  if (isProcessing) {
    console.log('[UploadProcessor] Already processing, new files will be picked up automatically');
    return [];
  }

  if (!currentApi) {
    console.error('[UploadProcessor] No API client set');
    return [];
  }

  isProcessing = true;
  const results: UploadedFile[] = [];

  // Start a timer to clean up expired entries periodically
  const cleanupInterval = setInterval(async () => {
    let changed = false;

    const removedDuplicates = await removeExpiredDuplicates();
    if (removedDuplicates > 0) {
      console.log(`[UploadProcessor] Removed ${removedDuplicates} expired duplicate entries`);
      changed = true;
    }

    // Also clean up failed uploads older than 1 hour
    const removedFailed = await removeExpiredFailedUploads();
    if (removedFailed > 0) {
      console.log(`[UploadProcessor] Removed ${removedFailed} expired failed uploads`);
      changed = true;
    }

    if (changed) {
      await notifyQueueChanged();
    }
  }, 5000); // Check every 5 seconds

  try {
    // Process in a loop to pick up any files added while we were processing
    while (true) {
      const pendingUploads = await getPendingUploads(organizationId);

      if (pendingUploads.length === 0) {
        console.log('[UploadProcessor] No pending uploads');
        break;
      }

      // Sort by filename for stable processing order
      pendingUploads.sort((a, b) => a.fileName.localeCompare(b.fileName, 'nb-NO'));

      console.log(`[UploadProcessor] Processing ${pendingUploads.length} uploads`);

      let completed = 0;
      const total = pendingUploads.length;

      for (const upload of pendingUploads) {
        try {
          // Mark as uploading
          await updateUploadStatus(upload.id, 'uploading');
          await notifyQueueChanged();

          // Convert to File and upload with the batch datetime
          const file = queuedUploadToFile(upload);
          const uploadResult = await uploadSingleFileWithDateTime(
            organizationId,
            file,
            upload.batchUploadDateTime,
            upload,
          );

          if (uploadResult) {
            if (uploadResult.duplicate) {
              // Mark as duplicate - keep in queue temporarily for display
              console.log(`[UploadProcessor] ${upload.fileName} is a duplicate`);
              await updateUploadStatus(upload.id, 'completed', {
                substanceUuid: uploadResult.uuid,
                isDuplicate: true,
                duplicateDetectedAt: Date.now(),
                displayError: t('substanceUpload.alreadyUploaded'),
              });
            } else {
              // Mark as completed and remove from queue
              await updateUploadStatus(upload.id, 'completed', {
                substanceUuid: uploadResult.uuid,
              });
              await removeUpload(upload.id);
            }
            results.push(uploadResult);
          } else {
            // Mark as failed
            await updateUploadStatus(upload.id, 'failed', {
              displayError: t('substanceUpload.uploadFailed'),
            });
          }
        } catch (error) {
          console.error(`[UploadProcessor] Failed to upload ${upload.fileName}:`, error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          const displayError = getDisplayError(errorMessage);
          await updateUploadStatus(upload.id, 'failed', {
            error: errorMessage,
            displayError,
            retryCount: (upload.retryCount ?? 0) + 1,
          });
        }

        completed++;
        onProgress?.(completed, total);
        await notifyQueueChanged();
      }
    }

    return results;
  } finally {
    clearInterval(cleanupInterval);
    // Final cleanup of expired entries
    await removeExpiredDuplicates();
    await removeExpiredFailedUploads();
    isProcessing = false;
    await notifyQueueChanged();
  }
}

/**
 * Convert technical error message to user-friendly display message.
 * Status code checks come BEFORE generic error type checks.
 */
function getDisplayError(error: string): string {
  // Log for debugging
  console.log(`[UploadProcessor] getDisplayError called with: "${error}"`);

  // Check for specific HTTP status codes first
  if (error.includes('401') || error.includes('Unauthorized')) {
    return t('substanceUpload.loginExpired');
  }
  if (error.includes('403') || error.includes('Forbidden')) {
    return t('substanceUpload.noAccess');
  }
  if (error.includes('404') || error.includes('Not Found')) {
    return t('substanceUpload.endpointNotFound');
  }
  if (error.includes('413') || error.includes('too large') || error.includes('Too Large')) {
    return t('substanceUpload.fileTooLarge');
  }
  if (error.includes('500') || error.includes('Internal Server Error')) {
    return t('substanceUpload.serverError');
  }
  if (error.includes('502') || error.includes('503') || error.includes('504')) {
    return t('substanceUpload.serverUnavailable');
  }

  // Check for timeout errors
  if (error.includes('timeout') || error.includes('Timeout') || error.includes('TIMEOUT')) {
    return t('substanceUpload.timeout');
  }

  // Check for network errors (after status codes, so "Upload failed: 403" doesn't match "failed")
  if (
    error.includes('network') ||
    error.includes('Network') ||
    error.includes('Failed to fetch') ||
    error.includes('NetworkError') ||
    error.includes('ERR_NETWORK')
  ) {
    return t('substanceUpload.networkError');
  }

  // Check for CORS errors
  if (error.includes('CORS') || error.includes('cross-origin')) {
    return t('substanceUpload.corsError');
  }

  return t('substanceUpload.genericError');
}

const MAX_UPLOAD_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

/**
 * Upload a single file to the server with the uploadDateTime query parameter.
 * Uses a custom fetch instead of the API client to add the query param.
 */
async function uploadSingleFileWithDateTime(
  organizationId: string,
  file: File,
  uploadDateTime: string | undefined,
  queuedUpload: QueuedUpload,
): Promise<UploadedFile | null> {
  const suffix = file.name.slice(-3).toLowerCase();
  if (suffix !== 'pdf' && file.type !== '') {
    console.warn(`[UploadProcessor] Skipping non-PDF file: ${file.name}`);
    return null;
  }

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_UPLOAD_RETRIES; attempt++) {
    try {
      // Build URL with uploadDateTime query parameter
      let url = `${getBasePath()}/organizations/${encodeURIComponent(organizationId)}/substances`;
      if (uploadDateTime) {
        url += `?uploadDateTime=${encodeURIComponent(uploadDateTime)}`;
      }

      // Get authorization header (supports both admin Basic auth and OAuth Bearer token)
      // Fix for issue #12431 - admin login was not working for uploads
      const authHeader = await getAuthorizationHeader();

      // Create form data with explicit filename (ensures Unicode encoding)
      const formData = new FormData();
      formData.append('filename', file, file.name);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: authHeader,
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result && result.length > 0) {
        return {
          filename: result[0].filename,
          uuid: result[0].uuid,
          duplicate: result[0].duplicate,
        };
      }

      return null;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Update retry count in queue
      await updateUploadStatus(queuedUpload.id, 'uploading', {
        retryCount: attempt,
      });

      // Check if it's an auth error (401/403) that might be fixed by retry
      const isAuthError =
        lastError.message.includes('401') ||
        lastError.message.includes('403') ||
        lastError.message.includes('Unauthorized') ||
        lastError.message.includes('Forbidden');

      if (isAuthError && attempt < MAX_UPLOAD_RETRIES) {
        console.warn(
          `[UploadProcessor] Auth error on attempt ${attempt}/${MAX_UPLOAD_RETRIES} for ${file.name}, retrying in ${RETRY_DELAY_MS}ms...`,
        );
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * attempt));
        continue;
      }

      // For non-auth errors or final attempt, throw
      throw lastError;
    }
  }

  // Should not reach here, but just in case
  throw lastError || new Error('Upload failed after retries');
}

/**
 * Retry all failed uploads for an organization
 */
export async function retryFailedUploads(organizationId: string): Promise<UploadedFile[]> {
  const failed = await getFailedUploads(organizationId);

  if (failed.length === 0) {
    console.log('[UploadProcessor] No failed uploads to retry');
    return [];
  }

  console.log(`[UploadProcessor] Retrying ${failed.length} failed uploads`);

  // Reset failed status to pending and clear error info
  for (const upload of failed) {
    await updateUploadStatus(upload.id, 'pending', {
      error: undefined,
      displayError: undefined,
      retryCount: 0,
    });
  }

  await notifyQueueChanged();

  // Process the queue
  return processUploads(organizationId);
}

/**
 * Check if there are pending uploads and process them
 * Called on app startup to resume interrupted uploads
 */
export async function resumePendingUploads(organizationId: string): Promise<void> {
  const pending = await getPendingUploads(organizationId);

  if (pending.length > 0) {
    console.log(`[UploadProcessor] Found ${pending.length} pending uploads, resuming...`);
    // Don't await - let it run in background
    processUploads(organizationId).catch((error) => {
      console.error('[UploadProcessor] Error resuming uploads:', error);
    });
  }
}

/**
 * Get the current processing state
 */
export function isProcessingUploads(): boolean {
  return isProcessing;
}
